import { useState, useMemo } from 'react';
import { useAppState } from '@/hooks/useAppState';
import TaskItem from '@/components/feature/TaskItem';
import QuickAddSheet from '@/components/feature/QuickAddSheet';
import FilterChips from '@/components/feature/FilterChips';
import EmptyState from '@/components/feature/EmptyState';
import StatusTag from '@/components/feature/StatusTag';

type Tab = 'today' | 'upcoming' | 'completed';

export default function Todo() {
  const {
    tasks,
    projects,
    toggleTask,
    addTask,
    editTask,
    postponeTask,
    deleteTask,
    todayTasks,
    upcomingTasks,
    completedTasks,
  } = useAppState();

  const [tab, setTab] = useState<Tab>('today');
  const [priorityFilter, setPriorityFilter] = useState('全部');
  const [projectFilter, setProjectFilter] = useState('全部');
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const handleEdit = (id: string) => {
    const task = tasks.find((item) => item.id === id);
    if (!task) return;
    const title = window.prompt('修改待办', task.title)?.trim();
    if (title && title !== task.title) editTask(id, { title });
  };

  const priorityOptions = [
    { label: '全部', value: '全部' },
    { label: '高', value: '高' },
    { label: '中', value: '中' },
    { label: '低', value: '低' },
  ];

  const projectOptions = useMemo(() => {
    const opts = [{ label: '全部', value: '全部' }];
    const names = [...new Set(tasks.map((t) => t.projectName).filter(Boolean))];
    names.forEach((n) => opts.push({ label: n, value: n }));
    return opts;
  }, [tasks]);

  const filtered = useMemo(() => {
    let list: typeof tasks = [];

    if (tab === 'today') list = todayTasks;
    else if (tab === 'upcoming') {
      const overdue = tasks.filter((t) => t.dueDate < today && !t.completed);
      list = [...overdue, ...upcomingTasks].sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );
    } else {
      list = completedTasks;
    }

    if (priorityFilter !== '全部') {
      list = list.filter((t) => t.priority === priorityFilter);
    }
    if (projectFilter !== '全部') {
      list = list.filter((t) => t.projectName === projectFilter);
    }

    return list;
  }, [tab, priorityFilter, projectFilter, todayTasks, upcomingTasks, completedTasks, tasks, today]);

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'today', label: '今天', count: todayTasks.length },
    { key: 'upcoming', label: '即将到期', count: tasks.filter((t) => !t.completed && t.dueDate !== today).length },
    { key: 'completed', label: '已完成', count: completedTasks.length },
  ];

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header */}
      <header className="px-4 pt-6 pb-2"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}>
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-foreground-800 font-heading">待办</h1>
          <button
            onClick={() => setQuickAddOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
          </button>
        </div>
      </header>

      {/* Tab switcher */}
      <div className="px-4 mb-3">
        <div className="flex bg-background-100 rounded-full p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 text-xs py-2 rounded-full font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === t.key
                  ? 'bg-background-50 text-foreground-800'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`ml-1 text-[10px] ${tab === t.key ? 'text-primary-600' : 'text-foreground-400'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-4 mb-1">
        <FilterChips
          options={priorityOptions}
          value={priorityFilter}
          onChange={setPriorityFilter}
        />
      </div>

      {/* Task list */}
      <div className="px-0">
        {filtered.length > 0 ? (
          <div className="divide-y divide-background-200/50">
            {filtered.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                onToggle={toggleTask}
                onEdit={handleEdit}
                onPostpone={postponeTask}
                onDelete={deleteTask}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-task-line"
            title={tab === 'completed' ? '还没有完成的任务' : '把脑中的事放下来，今天只做下一步。'}
            description={tab === 'completed' ? '完成一件就勾一件，看着列表清空很有成就感。' : '点击右上角的 + 号，快速记录你想做的事。'}
            action={tab !== 'completed' ? { label: '快速新增', onClick: () => setQuickAddOpen(true) } : undefined}
          />
        )}
      </div>

      {/* Completed section hint */}
      {tab !== 'completed' && filtered.length > 0 && (
        <div className="px-4 mt-4 pb-4">
          <p className="text-[10px] text-foreground-300 text-center">
            向左滑动任务可以编辑、延期或删除
          </p>
        </div>
      )}

      <QuickAddSheet
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onAdd={addTask}
        projects={projects.filter((p) => p.status === 'active').map((p) => ({ id: p.id, name: p.name }))}
      />
    </div>
  );
}
